import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { marked } from 'marked';
import { StyleClassModule } from 'primeng/styleclass';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { MessageModule } from 'primeng/message';

import { NovuNotificationModel } from '@shared/model/novu-notification-model';
import { Moment } from '@shared/pipe/moment.pipe';
import { TagComponent } from '@shared/component/tag/tag.component';
import { SeverityType } from '@shared/enum/severity-type';
import { NovuNotificationType } from '@shared/enum/novu-notification-type';
import { NovuActionType, NovuInboxFilter, NovuService } from '@core/service/novu.service';

// No usamos el Web Component de Novu (`@novu/js/ui`): esta UI consume el estado ya
// resuelto por `NovuService` (SDK headless `@novu/js`) y solo se encarga de pintarlo,
// combinando componentes de PrimeNG con markup a mano donde el diseño lo pide (las
// pestañas de filtro replican la píldora de Poseidon a medida, ver `tabButtonClass`).
// Diseño de referencia: panel de notificaciones de la plantilla Poseidon (PrimeNG) —
// panel `rounded-3xl` con pestañas No leídas/Archivadas/Todas y mensaje en tarjeta.
// `NovuService` ya resuelve su identidad de forma SSR-safe internamente y reacciona a los
// eventos de websocket, así que este componente no necesita guards de plataforma.
@Component({
  selector: 'app-notification-inbox',
  templateUrl: './notification-inbox.component.html',
  styleUrls: ['./notification-inbox.component.scss'],
  imports: [
    StyleClassModule,
    OverlayBadgeModule,
    ButtonModule,
    AvatarModule,
    ProgressSpinnerModule,
    ScrollPanelModule,
    MessageModule,
    Moment,
    TagComponent,
  ],
})
export class NotificationInboxComponent {
  private readonly novuService = inject(NovuService);

  protected readonly notifications = toSignal(this.novuService.notifications$, {
    initialValue: [] as NovuNotificationModel[],
  });
  protected readonly unreadCount = toSignal(this.novuService.unreadCount$, { initialValue: 0 });
  protected readonly loading = toSignal(this.novuService.loading$, { initialValue: false });
  protected readonly loadingMore = toSignal(this.novuService.loadingMore$, { initialValue: false });
  protected readonly hasMore = toSignal(this.novuService.hasMore$, { initialValue: false });
  protected readonly error = toSignal(this.novuService.error$, { initialValue: false });
  protected readonly filter = toSignal(this.novuService.filter$, { initialValue: 'unread' as NovuInboxFilter });
  protected readonly isEmpty = computed(() => !this.loading() && !this.error() && this.notifications().length === 0);
  protected readonly filters: Array<{ value: NovuInboxFilter; label: string }> = [
    { value: 'unread', label: 'No leídas' },
    { value: 'archived', label: 'Archivadas' },
    { value: 'all', label: 'Todas' },
  ];
  protected readonly actionType: Record<'PRIMARY' | 'SECONDARY', NovuActionType> = {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
  };
  protected readonly tagSeverity = SeverityType.INFO;

  // Ícono mostrado en el avatar según el `type` del payload del workflow (ver
  // NovuNotificationType). Si el payload no trae `type` (o llega un valor no
  // reconocido), se usa el ícono neutro por defecto.
  private readonly typeIcon: Record<NovuNotificationType, string> = {
    [NovuNotificationType.INFO]: 'pi pi-info-circle',
    [NovuNotificationType.SUCCESS]: 'pi pi-check-circle',
    [NovuNotificationType.WARNING]: 'pi pi-exclamation-triangle',
    [NovuNotificationType.ERROR]: 'pi pi-times-circle',
    [NovuNotificationType.QUESTION]: 'pi pi-question-circle',
    [NovuNotificationType.ALERT]: 'pi pi-bell',
  };

  // `p-badge` se renderiza dentro del template interno de `p-overlay-badge`
  // (ViewEncapsulation.None), así que el SCSS scoped de este componente no lo
  // alcanza. `pt` es el mecanismo soportado por PrimeNG para este caso (sin
  // `::ng-deep`): `OverlayBadge` reenvía `ptm('pcBadge')` al `p-badge` interno.
  protected readonly unreadBadgePt = {
    pcBadge: {
      root: {
        style: {
          width: '20px',
          height: '20px',
          minWidth: '20px',
          lineHeight: '20px',
          padding: '0',
          fontSize: '0.7rem',
        },
      },
    },
  };

  // Pestañas construidas a mano (no `p-selectbutton`) para replicar exactamente
  // la píldora de filtros del panel de notificaciones de Poseidon
  // (https://poseidon.primeng.org/): fondo translúcido, segmento activo en
  // `primary.100`/`primary.900` y hover suave en los inactivos.
  protected tabButtonClass(tab: NovuInboxFilter): string {
    const base = 'rounded-full p-2 flex-1 flex items-center justify-center transition-all hover:cursor-pointer';
    const active = 'bg-primary-100 dark:bg-primary-900';
    const inactive = 'hover:bg-primary-100/30 dark:hover:bg-primary-900/30';

    return `${base} ${this.filter() === tab ? active : inactive}`;
  }

  protected onOpen(): void {
    void this.novuService.loadInbox();
  }

  protected onFilterChange(filter: NovuInboxFilter): void {
    this.novuService.setFilter(filter);
  }

  protected onNotificationClick(notification: NovuNotificationModel): void {
    if (!notification.getIsRead()) {
      void this.novuService.markAsRead(notification.getId());
    }

    const redirectUrl = notification.getRedirectUrl();

    if (redirectUrl) {
      globalThis.location.href = redirectUrl;
    }
  }

  protected onMarkAsRead(event: Event, notification: NovuNotificationModel): void {
    event.stopPropagation();
    void this.novuService.markAsRead(notification.getId());
  }

  protected onArchiveToggle(event: Event, notification: NovuNotificationModel): void {
    event.stopPropagation();

    if (this.filter() === 'archived') {
      void this.novuService.unarchive(notification.getId());
      return;
    }

    void this.novuService.archive(notification.getId());
  }

  protected onMarkAllAsRead(): void {
    void this.novuService.markAllAsRead();
  }

  protected onLoadMore(): void {
    void this.novuService.loadMore();
  }

  protected onDelete(event: Event, notification: NovuNotificationModel): void {
    event.stopPropagation();
    void this.novuService.deleteNotification(notification.getId());
  }

  protected onCompleteAction(event: Event, notification: NovuNotificationModel, actionType: NovuActionType): void {
    event.stopPropagation();
    void this.novuService.completeAction(notification.getId(), actionType);
  }

  protected notificationIcon(notification: NovuNotificationModel): string {
    const type = notification.getType();
    return type ? this.typeIcon[type] : 'pi pi-bell';
  }

  protected notificationTypeClass(notification: NovuNotificationModel): string {
    return `notification-inbox-item-icon type-${(notification.getType() ?? NovuNotificationType.INFO).toLowerCase()}`;
  }

  // Novu permite Markdown en el cuerpo de las plantillas (p. ej. `**texto**`); `[innerHTML]`
  // sanitiza automáticamente el resultado, así que no requiere `DomSanitizer` explícito.
  protected renderBody(body: string): string {
    return marked.parseInline(body, { async: false });
  }
}
